/**
 * Componente para mostrar la lista de categorías.
 *
 * Este componente renderiza una tabla con todas las categorías del sistema,
 * incluyendo funcionalidades de paginación, acciones de edición, eliminación
 * y navegación a subcategorías. Maneja permisos de usuario y formateo de datos.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-05]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PropTypes from "prop-types";
import { Pagination, Stack } from "@mui/material";
import usePermission from "hooks/usePermission";
import { fetchWrapper } from "fetchWrapper";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import { useMaterialUIController } from "context";
import colors from "assets/theme/base/colors"

export default function CategoryList({
  handleCategoryCreate,
  handleCategoryEdit,
  handleCategoryDelete,
  entriesStart,
  entriesEnd,
  totalData,
  totalPages,
  currentPage,
  handlePageChange,
  callHandleClickSubmit,
  categoriesList = [],
  handleAlert = () => { },
}) {
  // Verificar permisos
  const canCreate = usePermission("Crear", "Categorías");
  const canEdit = usePermission("Editar", "Categorías");
  const canDelete = usePermission("Eliminar", "Categorías");
  const canListSubcategories = usePermission("Listar", "Subcategorías");
  const navigate = useNavigate();
  let { white, black, text } = colors

  black = black.main;
  white = white.main;
  const iconColor = text.main; // #7b809a - mismo color que el texto de las tablas

  // Obtener el estado del tema (modo oscuro/claro)
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [modalOpen, setModalOpen] = useState(false);
  const [currentParticipant, setCurrentParticipant] = useState(null);

  /**
   * Formatea una fecha para mostrarla en formato legible
   * @param {string} dateString - Fecha en formato string
   * @returns {string} Fecha formateada
   */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    // El backend envía la fecha en hora local de Colombia
    // Solo necesitamos formatearla sin convertir zona horaria
    const dateStr = dateString.replace('T', ' ').split('.')[0];

    // Validar que la fecha tenga el formato esperado
    if (!/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/.test(dateStr)) {
      return "Fecha inválida";
    }

    return dateStr;
  };

  /**
   * Navega a la vista de subcategorías de una categoría específica
   * @param {number} categoryId - ID de la categoría
   */
  const handleViewSubcategories = (path, title) => {
    navigate(path, { state: { categoryTitle: title } });
  };

  const columns = [
    { Header: "Nombre", accessor: "name", align: "left" },
    { Header: "Creado", accessor: "created", align: "center" },
    { Header: "Modificado", accessor: "modified", align: "center" },
    { Header: "Creado por", accessor: "created_by", align: "center" },
    { Header: "Modificado por", accessor: "modified_by", align: "center" },
    { Header: "Acciones", accessor: "action", width: "12%", align: "center", disableSortBy: true },
  ];

  const rows = categoriesList.map((category) => ({
    name: category.name,
    created: formatDate(category.created),
    modified: formatDate(category.modified),
    created_by: category.created_by?.email || "N/A",
    modified_by: category.modified_by?.email || "N/A",
    action: (
      <MDBox style={{ display: "flex", gap: "10px" }}>
        {canListSubcategories && (
          <a
            onClick={() => handleViewSubcategories(`/Categorias-S/${category.id}`, category.name)}
            style={{ cursor: "pointer" }}>
            <CategoryOutlinedIcon
              titleAccess="Ver subcategorías"
              style={{
                fontSize: "24px",
                width: "24px",
                height: "24px",
                color: darkMode ? white : iconColor,
              }}
            />
          </a>
        )}
        {canEdit && (
          <a onClick={() => handleCategoryEdit(category)} style={{ cursor: "pointer" }}>
            <EditOutlinedIcon
              titleAccess="Editar"
              style={{
                fontSize: "24px",
                width: "24px",
                height: "24px",
                color: darkMode ? white : iconColor,
              }}
            />
          </a>
        )}
        {canDelete && (
          <a onClick={() => handleCategoryDelete(category)} style={{ cursor: "pointer" }}>
            <DeleteForeverOutlinedIcon
              titleAccess="Eliminar"
              style={{
                fontSize: "200px",
                width: "24px",
                height: "24px",
                color: darkMode ? white : iconColor,
              }}
            />
          </a>
        )}
      </MDBox>
    ),
  }));

  return (
    <>
      {/* Botón "Ver participantes" encima de los otros botones */}
      {canCreate && (
        <MDBox style={{ display: "flex", justifyContent: "flex-end", marginRight: "2vw" }}>
          <MDButton onClick={handleCategoryCreate} variant="contained" color="info">
            Crear
          </MDButton>
        </MDBox>
      )}
      <DataTable
        table={{ columns, rows }}
        isSorted={true}
        entriesPerPage={false}
        showTotalEntries={false}
        canSearch={false}
        noEndBorder={false}
      />
      {!categoriesList || categoriesList.length === 0 ? (
        <MDBox style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <MDTypography
            variant="button"
            color="secondary"
            fontWeight="regular"
            style={{ fontSize: "20px", fontFamily: "Roboto, sans-serif" }}>
            No hay datos
          </MDTypography>
        </MDBox>
      ) : (
        <>
          {/* Mostrar la paginación */}
          <MDBox style={{ marginLeft: "20px" }} mb={{ xs: 3, sm: 0 }}>
            <MDTypography
              style={{ left: "20px" }}
              variant="button"
              fontWeight="regular">
              Mostrando {entriesStart} hasta {entriesEnd} de {totalData} resultados en total
            </MDTypography>
          </MDBox>
          <Stack style={{ marginLeft: "20px", marginBottom: "10px" }} spacing={2}>
            <Pagination
              color={darkMode ? "white" : "standard"}
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}></Pagination>
          </Stack>{" "}
        </>
      )}
    </>
  );
}

CategoryList.propTypes = {
  handleCategoryCreate: PropTypes.func.isRequired,
  handleCategoryEdit: PropTypes.func.isRequired,
  handleCategoryDelete: PropTypes.func.isRequired,
  entriesStart: PropTypes.number,
  entriesEnd: PropTypes.number,
  totalData: PropTypes.number,
  totalPages: PropTypes.number,
  currentPage: PropTypes.number,
  handlePageChange: PropTypes.func,
  callHandleClickSubmit: PropTypes.func,
  categoriesList: PropTypes.array,
  handleAlert: PropTypes.func,
};
